import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, RefreshCw, X, Focus, LayoutGrid, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  CoachPanel, GuidePanel, ConflictPanel, ConflictCoachPanel, OL, ROUND_RESET,
} from '@/components/OperationListenParts';
import { OperationListenDebrief } from '@/components/OperationListenDebrief';
import { VoiceCaptureControl } from '@/components/VoiceCaptureControl';
import {
  ConflictCheckInCard,
  ConflictCoachCheckInCard,
  WaitingForHumansCard,
} from '@/components/OperationListenRoundEnd';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 1500;

const authHeader = () => {
  const t = localStorage.getItem('gntl_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

async function api(path, opts = {}) {
  const res = await fetch(`${API}/api/olg${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeader(), ...(opts.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `Request failed (${res.status})`);
  return data;
}

const OperationListenMasterView = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState(null);
  const [room, setRoom] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0-3 for Coach/Guide/Conflict/CC
  const [focusMode, setFocusMode] = useState(false); // when true, show only the active panel (works on desktop too)
  const [preRoleMode, setPreRoleMode] = useState('4p'); // '4p' | '3p' | '2p' — chosen before entering
  const [tcSessionCode] = useState(() => new URLSearchParams(window.location.search).get('tcSession'));
  const [debrief, setDebrief] = useState(null); // active debrief object or null
  const timer = useRef(null);

  // Bind this demo room to the Teacher's Corner session so students in the
  // lounge can watch the live drill. Runs whenever a new room code is created.
  useEffect(() => {
    if (!code || !tcSessionCode) return;
    fetch(`${API}/api/tc/session/${tcSessionCode}/demo-room`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body: JSON.stringify({ demo_room_code: code }),
    }).catch(() => {});
  }, [code, tcSessionCode]);

  const refresh = useCallback(async () => {
    if (!code) return;
    try {
      const r = await api(`/rooms/${code}`);
      setRoom(r);
      setError(null);
    } catch (e) {
      setError(e.message);
      if (String(e.message).toLowerCase().includes('not found')) {
        setCode(null); setRoom(null);
      }
    }
  }, [code]);

  useEffect(() => {
    if (!code) return undefined;
    refresh();
    timer.current = setInterval(refresh, POLL_MS);
    return () => clearInterval(timer.current);
  }, [code, refresh]);

  const createDemo = async () => {
    setBusy(true); setError(null);
    try {
      const pc = preRoleMode === '4p' ? 4 : (preRoleMode === '3p' ? 3 : 2);
      const r = await api('/admin/demo-room', {
        method: 'POST',
        body: JSON.stringify({ playerCount: pc }),
      });
      setCode(r.code);
      setRoom(r);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const closeDemo = async () => {
    if (!code) return;
    try { await api(`/admin/demo-room/${code}/end`, { method: 'POST' }); } catch (_) {}
    if (tcSessionCode) {
      // Unbind from the Teacher's Corner session so students go back to the deck view.
      fetch(`${API}/api/tc/session/${tcSessionCode}/demo-room`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ demo_room_code: null }),
      }).catch(() => {});
    }
    setCode(null); setRoom(null);
  };

  const writeAs = (asRole) => async (patch) => {
    if (!code) return;
    try {
      await api(`/admin/demo-room/${code}/write`, {
        method: 'POST',
        body: JSON.stringify({ asRole, patch }),
      });
      refresh();
    } catch (e) { setError(e.message); }
  };

  // Restart demo — archives the finished round to olg_debriefs (events +
  // state snapshot per the post-game analysis design doc), then clears every
  // round-scoped field. Preserves the room code, chosen game mode, and role
  // mode so students in the lounge stay bound.
  //
  // When `openDebrief` is true (default), the archived debrief is shown
  // immediately. When false (used by "Start new round"), the debrief is
  // archived silently so the facilitator can still open it later via the
  // 📄 button, but the overlay stays closed.
  const restartDemo = useCallback(async ({ openDebrief = true } = {}) => {
    if (!code) { refresh(); return; }
    try {
      let archived = null;
      try {
        const resp = await api(`/admin/demo-room/${code}/archive-round`, { method: 'POST' });
        if (resp && resp.debrief) archived = resp.debrief;
      } catch (_) { /* noop */ }
      await api(`/admin/demo-room/${code}/write`, {
        method: 'POST',
        body: JSON.stringify({ asRole: 'coach', patch: ROUND_RESET }),
      });
      refresh();
      if (archived && openDebrief) setDebrief(archived);
    } catch (e) { setError(e.message); }
  }, [code, refresh]);

  // Human check-in submit — PATCHes the active debrief doc. The backend
  // recomputes the 70/30 blend and returns the new scores.
  const submitHumanAnswer = useCallback(async ({ role, q1, q2, q3 }) => {
    if (!debrief) return null;
    const updated = await api(`/debriefs/${debrief.id}/human-answer`, {
      method: 'POST',
      body: JSON.stringify({ role, q1, q2, q3 }),
    });
    setDebrief(updated);
    return updated;
  }, [debrief]);

  // Last debrief — surfaces the most recent archived round so facilitators
  // can re-open it after dismissing. Returns null if the room has never been
  // reset.
  const openLatestDebrief = useCallback(async () => {
    if (!code) return;
    try {
      const resp = await api(`/admin/demo-room/${code}/debriefs?limit=1`);
      if (resp.debriefs && resp.debriefs.length) setDebrief(resp.debriefs[0]);
      else setError('No archived rounds yet — play, then press ⟳ to see the debrief.');
    } catch (e) { setError(e.message); }
  }, [code]);

  const unbindTcDemo = useCallback(() => {
    if (!tcSessionCode) return;
    // Fire-and-forget so navigation isn't blocked. Uses keepalive so it survives page unload.
    try {
      fetch(`${API}/api/tc/session/${tcSessionCode}/demo-room`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ demo_room_code: null }),
        keepalive: true,
      });
    } catch (_) {}
  }, [tcSessionCode]);

  const goBackToOrigin = useCallback(() => {
    unbindTcDemo();
    navigate(tcSessionCode ? '/admin/teachers-corner' : '/admin/dashboard');
  }, [navigate, tcSessionCode, unbindTcDemo]);

  // Auto-unbind on unmount / page unload so students in the lounge fall back to
  // the deck view instead of seeing a stale demo room screen.
  useEffect(() => {
    const onUnload = () => unbindTcDemo();
    window.addEventListener('beforeunload', onUnload);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      unbindTcDemo();
    };
  }, [unbindTcDemo]);

  // Round-complete detection — drives the per-panel inline check-in /
  // waiting cards. Declared before the landing early-return so hook order
  // stays stable.
  const roundComplete = !!(room?.state?.paraphraseSubmitted
    && room?.state?.respectComplete
    && room?.state?.nextStepsComplete);

  // ---------- Landing (before demo room created) ----------
  if (!code || !room) {
    return (
      <div data-ol-dark style={{ minHeight: "100vh", background: OL.bg, color: OL.text, padding: 24, paddingTop: 'max(24px, env(safe-area-inset-top))' }}>
        <div style={{ maxWidth: 460, margin: '0 auto' }}>
          <button
            onClick={goBackToOrigin}
            style={{ color: OL.textDim, background: 'transparent', border: 0, display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 28, cursor: 'pointer' }}
            data-testid="master-back"
          >
            <ArrowLeft size={16} /> {tcSessionCode ? "Teacher's Corner" : 'Admin dashboard'}
          </button>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: OL.clay, marginBottom: 8 }}>Master View</div>
          <h1 style={{ fontFamily: '"DM Serif Display", Georgia, serif', fontSize: 32, color: OL.cream, marginBottom: 12, lineHeight: 1.1 }}>
            Operation Listen — four seats, one screen.
          </h1>
          <p style={{ fontSize: 14, color: OL.textDim, lineHeight: 1.6, marginBottom: 24 }}>
            Spin up a demo room with four dummy players so you can operate every role's
            perspective side by side. Useful for facilitator training, dry-runs, and screen
            recordings.
          </p>

          {/* Pre-game: Mode (2-Player, 3-Player, 4-Player). Smaller modes
              hide seats that aren't needed (CC + optionally Coach). */}
          <div style={{ marginBottom: 20 }} data-testid="master-role-mode-picker">
            <div style={{ fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: OL.textMuted, fontWeight: 600, marginBottom: 8 }}>
              Mode
            </div>
            <div style={{
              display: 'inline-flex', gap: 2, background: OL.surface, border: `1px solid ${OL.border2}`,
              borderRadius: 999, padding: 3,
            }}>
              {[
                { key: '4p', label: '4-Player' },
                { key: '3p', label: '3-Player' },
                { key: '2p', label: '2-Player' },
              ].map((opt) => {
                const active = preRoleMode === opt.key;
                return (
                  <button
                    key={opt.key}
                    onClick={() => setPreRoleMode(opt.key)}
                    data-testid={`master-role-mode-${opt.key}`}
                    data-active={active ? 'true' : 'false'}
                    style={{
                      padding: '8px 14px', borderRadius: 999, border: 0, cursor: 'pointer',
                      background: active ? OL.clay : 'transparent',
                      color: active ? '#fff' : OL.textDim, fontSize: 12, fontWeight: 600,
                    }}
                  >{opt.label}</button>
                );
              })}
            </div>
            <p style={{ fontSize: 11, color: OL.textMuted, marginTop: 8, lineHeight: 1.5 }}>
              {preRoleMode === '4p'
                ? 'Four panels — Coach observes, Guide listens, Conflict emotes, Conflict Coach steers.'
                : preRoleMode === '3p'
                ? 'Three panels — Coach, Guide, and Conflict. No Conflict Coach seat.'
                : 'Two panels — Guide and Conflict. Guide runs the drill themselves.'}
            </p>
          </div>

          {/* Pre-game: Guide picks their in-round view (ladder vs meter-only). */}
          <button
            onClick={createDemo}
            disabled={busy}
            style={{
              width: '100%', padding: '16px 20px', borderRadius: 999,
              background: OL.clay, color: '#fff', fontSize: 15, fontWeight: 600,
              border: 0, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1,
            }}
            data-testid="master-create"
          >{busy ? 'Creating…' : 'Create demo room'}</button>
          {error && <p style={{ color: '#F87171', fontSize: 13, marginTop: 16 }} data-testid="master-error">{error}</p>}
        </div>
      </div>
    );
  }

  // ---------- Active demo (2 / 3 / 4 panels) ----------
  const playerCount = room?.state?.playerCount
    || (room?.state?.roleMode === '2p' ? 2 : room?.state?.roleMode === '3p' ? 3 : 4);
  const is2p = playerCount === 2;
  const is3p = playerCount === 3;
  return (
    <div data-ol-dark style={{
      minHeight: '100vh', background: OL.bg, color: OL.text,
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Top bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 16px', height: 52, borderBottom: `1px solid ${OL.border}`,
        background: OL.surface, position: 'sticky', top: 0, zIndex: 10,
      }}>
        <button
          onClick={goBackToOrigin}
          style={{ color: OL.textDim, background: 'transparent', border: 0, display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}
          data-testid="master-back-2"
        >
          <ArrowLeft size={16} /> {tcSessionCode ? "Teacher's Corner" : 'Dashboard'}
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {tcSessionCode && (
            <span
              data-testid="master-tc-mirror-badge"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '5px 10px', borderRadius: 999,
                background: `${OL.clay}22`, border: `1px solid ${OL.clay}66`,
                color: OL.clay, fontSize: 10, fontWeight: 600,
                letterSpacing: '0.14em', textTransform: 'uppercase',
              }}
            >
              <span style={{
                width: 6, height: 6, borderRadius: '50%', background: OL.clay,
                animation: 'ol-pulse 1.6s ease-in-out infinite',
              }} />
              Mirroring · TC {tcSessionCode}
            </span>
          )}
          <button
            onClick={() => setFocusMode((v) => !v)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 12px', borderRadius: 999,
              background: focusMode ? OL.clay : 'transparent',
              border: `1px solid ${focusMode ? OL.clay : OL.border2}`,
              color: focusMode ? '#fff' : OL.textDim,
              fontSize: 11, fontWeight: 600, cursor: 'pointer',
              letterSpacing: '0.08em', textTransform: 'uppercase',
            }}
            title={focusMode ? 'Show all four panels' : 'Focus one role at a time'}
            data-testid="master-focus-toggle"
          >
            {focusMode ? <LayoutGrid size={12} /> : <Focus size={12} />}
            {focusMode ? 'Show all' : 'Focus one'}
          </button>
          <span style={{ fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: OL.textMuted }}>Demo room</span>
          <span style={{ fontFamily: 'monospace', fontSize: 16, letterSpacing: '0.3em', color: OL.cream }}>{code}</span>
          {/* Beginner ↔ Advanced switcher — showcasing helper for demo rooms. */}
          {room?.state && (
            <div style={{
              display: 'inline-flex', gap: 2, background: OL.surface2,
              border: `1px solid ${OL.border2}`, borderRadius: 999, padding: 2, marginLeft: 8,
            }} data-testid="master-gamemode-picker">
              {[
                { key: 'beginner', label: 'Beginner' },
                { key: 'advanced', label: 'Advanced' },
              ].map((opt) => {
                const active = (room.state.gameMode || 'beginner') === opt.key;
                return (
                  <button
                    key={opt.key}
                    onClick={async () => {
                      try {
                        await api(`/admin/demo-room/${code}/write`, {
                          method: 'POST',
                          body: JSON.stringify({ asRole: 'coach', patch: { gameMode: opt.key } }),
                        });
                        refresh();
                      } catch (_) {}
                    }}
                    data-testid={`master-gamemode-${opt.key}`}
                    style={{
                      padding: '4px 10px', borderRadius: 999, border: 0, cursor: 'pointer',
                      background: active ? OL.clay : 'transparent',
                      color: active ? '#fff' : OL.textDim, fontSize: 10, fontWeight: 700,
                      letterSpacing: '0.08em', textTransform: 'uppercase',
                    }}
                  >{opt.label}</button>
                );
              })}
            </div>
          )}
          <button
            onClick={openLatestDebrief}
            title="Open last round's debrief report"
            style={{ padding: 6, borderRadius: 999, background: OL.surface2, border: `1px solid ${OL.border2}`, color: OL.textDim, cursor: 'pointer' }}
            data-testid="master-debrief"
          ><FileText size={14} /></button>
          <button
            onClick={() => restartDemo()}
            title="Restart demo — archives round, opens debrief, then clears state"
            style={{ padding: 6, borderRadius: 999, background: OL.surface2, border: `1px solid ${OL.border2}`, color: OL.textDim, cursor: 'pointer' }}
            data-testid="master-refresh"
          ><RefreshCw size={14} /></button>
          <button
            onClick={closeDemo}
            style={{ padding: 6, borderRadius: 999, background: 'rgba(155,37,37,0.12)', border: `1px solid ${OL.danger}66`, color: '#F87171', cursor: 'pointer' }}
            data-testid="master-close"
          ><X size={14} /></button>
        </div>
      </div>

      {/* Tab nav — shown on mobile always, and on desktop when focusMode is on */}
      <div className={`ol-tabnav ${focusMode ? 'ol-tabnav-force' : ''}`} style={{
        display: focusMode ? 'flex' : 'none', gap: 0, borderBottom: `1px solid ${OL.border}`,
        background: OL.surface, position: 'sticky', top: 52, zIndex: 9,
      }}>
        {(is2p
            ? ['Guide', 'Conflict']
            : is3p
            ? ['Coach', 'Guide', 'Conflict']
            : ['Coach', 'Guide', 'Conflict', 'CC']).map((label, i) => (
          <button
            key={i}
            onClick={() => setActiveTab(i)}
            style={{
              flex: 1, padding: '10px 6px', background: 'transparent',
              border: 0, borderBottom: `2px solid ${activeTab === i ? OL.clay : 'transparent'}`,
              color: activeTab === i ? OL.cream : OL.textDim, fontSize: 12, fontWeight: 600,
              cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.14em',
            }}
            data-testid={`master-tab-${i}`}
          >{label}</button>
        ))}
      </div>

      {/* Panels — 2 / 3 / 4 side-by-side; 1-at-a-time when focusMode is on */}
      <div className={`ol-panels ${focusMode ? 'ol-panels-focus' : ''} ${is2p ? 'ol-panels-2p' : ''} ${is3p ? 'ol-panels-3p' : ''}`} style={{
        flex: 1, display: 'grid',
        gridTemplateColumns: focusMode
          ? '1fr'
          : (is2p
              ? 'repeat(2, minmax(320px, 1fr))'
              : is3p
              ? 'repeat(3, minmax(300px, 1fr))'
              : 'repeat(4, minmax(300px, 1fr))'),
        gap: 12, padding: 12, minHeight: 0,
      }}>
        {is2p ? (
          // 2P · Guide + Conflict — Guide runs the round themselves (Coach controls on their screen).
          <>
            <div className={`ol-panel-wrap ${(!focusMode || activeTab === 0) ? '' : 'ol-hide-focus'} ${activeTab === 0 ? '' : 'ol-hide-mobile'}`} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, width: '100%' }}>
              {roundComplete && (
                <WaitingForHumansCard roomCode={code} isDemoRoom roleMode="2p"
                  onOpenDebrief={(d) => setDebrief(d)} />
              )}
              <div style={{ width: '100%', display: 'flex', minWidth: 0, flex: 1 }}>
                <CoachPanel state={room.state} write={writeAs('coach')} testPrefix="master" panelTitle="Guide"
                  voiceCapture={<VoiceCaptureControl actor="guide" roomCode={code} isDemoRoom activeRound={!roundComplete && !debrief} testId="master-voice-guide" roomState={room.state} writeState={writeAs('guide')} />} />
              </div>
            </div>
            <div className={`ol-panel-wrap ${(!focusMode || activeTab === 1) ? '' : 'ol-hide-focus'} ${activeTab === 1 ? '' : 'ol-hide-mobile'}`} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, width: '100%' }}>
              {roundComplete && (
                <ConflictCheckInCard roomCode={code} isDemoRoom
                  onOpenDebrief={(d) => setDebrief(d)} />
              )}
              <div style={{ width: '100%', display: 'flex', minWidth: 0, flex: 1 }}>
                <ConflictPanel state={room.state} write={writeAs('playerB')} testPrefix="master"
                  voiceCapture={<VoiceCaptureControl actor="playerB" roomCode={code} isDemoRoom activeRound={!roundComplete && !debrief} testId="master-voice-conflict" roomState={room.state} writeState={writeAs('playerB')} />} />
              </div>
            </div>
          </>
        ) : is3p ? (
          // 3P · Coach + Guide + Conflict — no Conflict Coach seat.
          <>
            <div className={`ol-panel-wrap ${(!focusMode || activeTab === 0) ? '' : 'ol-hide-focus'} ${activeTab === 0 ? '' : 'ol-hide-mobile'}`} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, width: '100%' }}>
              {roundComplete && (
                <WaitingForHumansCard roomCode={code} isDemoRoom roleMode="3p"
                  onOpenDebrief={(d) => setDebrief(d)} />
              )}
              <div style={{ width: '100%', display: 'flex', minWidth: 0, flex: 1 }}>
                <CoachPanel state={room.state} write={writeAs('coach')} testPrefix="master" />
              </div>
            </div>
            <div className={`ol-panel-wrap ${(!focusMode || activeTab === 1) ? '' : 'ol-hide-focus'} ${activeTab === 1 ? '' : 'ol-hide-mobile'}`} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, width: '100%' }}>
              {roundComplete && (
                <WaitingForHumansCard roomCode={code} isDemoRoom roleMode="3p"
                  onOpenDebrief={(d) => setDebrief(d)} />
              )}
              <div style={{ width: '100%', display: 'flex', minWidth: 0, flex: 1 }}>
                <GuidePanel state={room.state} write={writeAs('guide')} testPrefix="master"
                  voiceCapture={<VoiceCaptureControl actor="guide" roomCode={code} isDemoRoom activeRound={!roundComplete && !debrief} testId="master-voice-guide" roomState={room.state} writeState={writeAs('guide')} />} />
              </div>
            </div>
            <div className={`ol-panel-wrap ${(!focusMode || activeTab === 2) ? '' : 'ol-hide-focus'} ${activeTab === 2 ? '' : 'ol-hide-mobile'}`} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, width: '100%' }}>
              {roundComplete && (
                <ConflictCheckInCard roomCode={code} isDemoRoom
                  onOpenDebrief={(d) => setDebrief(d)} />
              )}
              <div style={{ width: '100%', display: 'flex', minWidth: 0, flex: 1 }}>
                <ConflictPanel state={room.state} write={writeAs('playerB')} testPrefix="master"
                  voiceCapture={<VoiceCaptureControl actor="playerB" roomCode={code} isDemoRoom activeRound={!roundComplete && !debrief} testId="master-voice-conflict" roomState={room.state} writeState={writeAs('playerB')} />} />
              </div>
            </div>
          </>
        ) : (
          <>
            <div className={`ol-panel-wrap ${(!focusMode || activeTab === 0) ? '' : 'ol-hide-focus'} ${activeTab === 0 ? '' : 'ol-hide-mobile'}`} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, width: '100%' }}>
              {roundComplete && (
                <WaitingForHumansCard roomCode={code} isDemoRoom roleMode="4p"
                  onOpenDebrief={(d) => setDebrief(d)} />
              )}
              <div style={{ width: '100%', display: 'flex', minWidth: 0, flex: 1 }}>
                {/* Coach panel: no voice capture — only Guide and Conflict are measured. */}
                <CoachPanel state={room.state} write={writeAs('coach')} testPrefix="master" />
              </div>
            </div>
            <div className={`ol-panel-wrap ${(!focusMode || activeTab === 1) ? '' : 'ol-hide-focus'} ${activeTab === 1 ? '' : 'ol-hide-mobile'}`} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, width: '100%' }}>
              {roundComplete && (
                <WaitingForHumansCard roomCode={code} isDemoRoom roleMode="4p"
                  onOpenDebrief={(d) => setDebrief(d)} />
              )}
              <div style={{ width: '100%', display: 'flex', minWidth: 0, flex: 1 }}>
                <GuidePanel state={room.state} write={writeAs('guide')} testPrefix="master"
                  voiceCapture={<VoiceCaptureControl actor="guide" roomCode={code} isDemoRoom activeRound={!roundComplete && !debrief} testId="master-voice-guide" roomState={room.state} writeState={writeAs('guide')} />} />
              </div>
            </div>
            <div className={`ol-panel-wrap ${(!focusMode || activeTab === 2) ? '' : 'ol-hide-focus'} ${activeTab === 2 ? '' : 'ol-hide-mobile'}`} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, width: '100%' }}>
              {roundComplete && (
                <ConflictCheckInCard roomCode={code} isDemoRoom
                  onOpenDebrief={(d) => setDebrief(d)} />
              )}
              <div style={{ width: '100%', display: 'flex', minWidth: 0, flex: 1 }}>
                <ConflictPanel state={room.state} write={writeAs('playerB')} testPrefix="master"
                  voiceCapture={<VoiceCaptureControl actor="playerB" roomCode={code} isDemoRoom activeRound={!roundComplete && !debrief} testId="master-voice-conflict" roomState={room.state} writeState={writeAs('playerB')} />} />
              </div>
            </div>
            <div className={`ol-panel-wrap ${(!focusMode || activeTab === 3) ? '' : 'ol-hide-focus'} ${activeTab === 3 ? '' : 'ol-hide-mobile'}`} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, width: '100%' }}>
              {roundComplete && (
                <ConflictCoachCheckInCard roomCode={code} isDemoRoom
                  onOpenDebrief={(d) => setDebrief(d)} />
              )}
              <div style={{ width: '100%', display: 'flex', minWidth: 0, flex: 1 }}>
                {/* Conflict Coach panel: no voice capture — only Guide and Conflict are measured. */}
                <ConflictCoachPanel state={room.state} write={writeAs('conflict')} testPrefix="master" />
              </div>
            </div>
          </>
        )}
      </div>

      {error && <p style={{ color: '#F87171', fontSize: 13, padding: 12 }} data-testid="master-inline-error">{error}</p>}

      {/* Responsive rules */}
      <style>{`
        .ol-panels { overflow-x: auto; }
        .ol-panels-focus { max-width: 720px; margin: 0 auto; }
        .ol-hide-focus { display: none !important; }
        @media (max-width: 1200px) {
          .ol-panels:not(.ol-panels-focus):not(.ol-panels-2p):not(.ol-panels-3p) {
            grid-template-columns: repeat(4, minmax(280px, 1fr)) !important;
          }
          .ol-panels.ol-panels-3p:not(.ol-panels-focus) {
            grid-template-columns: repeat(3, minmax(260px, 1fr)) !important;
          }
        }
        @media (max-width: 900px) {
          .ol-panels:not(.ol-panels-focus) {
            grid-template-columns: 1fr !important;
            overflow-x: visible !important;
          }
          .ol-tabnav { display: flex !important; }
          .ol-hide-mobile { display: none !important; }
        }
      `}</style>

      {/* Post-game analysis overlay (design doc §12). Opens on restart,
          and can be re-opened via the master-debrief button. Each role's
          inline check-in card (see panel headers) is the primary surface
          now — this overlay is for a full-screen read of the final report. */}
      {debrief && (
        <OperationListenDebrief
          debrief={debrief}
          onSubmitHumanAnswer={submitHumanAnswer}
          onClose={() => setDebrief(null)}
          onStartNewRound={async () => {
            await restartDemo({ openDebrief: false });
            setDebrief(null);
          }}
        />
      )}
    </div>
  );
};

export default OperationListenMasterView;
